import { type JSX } from 'react';
import { Link } from 'react-router-dom';
import { Sparkles, X } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { useTokenStore } from '../api/use-api.ts';
import { dismissAutoMasteryNotice } from '../api/me.ts';
import { useOptimisticDismissal } from '../lib/use-optimistic-dismissal.ts';
import { PartyName, buttonClass } from './ui/index.ts';
import { SaveError } from './SaveError.tsx';

/**
 * Completing a session now masters targets that meet their rule (FB-0111), so whoever can complete
 * one is told once (owner 2026-10-26). The server decides who: `autoMasteryNoticeDue`.
 */
export function AutoMasteryNoticeBanner({
  practiceId,
  practiceName,
  canChangeSetting,
}: {
  readonly practiceId: string;
  readonly practiceName: string;
  /** The switch is the owner's (practice.update); everyone else is only told. */
  readonly canChangeSetting: boolean;
}): JSX.Element | null {
  const store = useTokenStore();
  const qc = useQueryClient();
  const dismissal = useOptimisticDismissal(
    (_: 'auto-mastery') => dismissAutoMasteryNotice(store),
    () => { void qc.invalidateQueries({ queryKey: ['me'] }); },
  );
  if (dismissal.hidden('auto-mastery')) return null;

  return (
    <div
      role="status"
      data-testid="auto-mastery-notice"
      className="border-b border-primary/30 bg-primary/10 px-4 py-2 text-sm text-foreground"
    >
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Sparkles className="h-4 w-4 shrink-0 text-primary" aria-hidden="true" />
          <span>
            New at <PartyName>{practiceName}</PartyName>: when you complete a session, a target you recorded on
            whose data now meets its mastery rule is marked mastered automatically. The case&rsquo;s lead practitioner
            (or, if the lead has left, the practitioners on the case) is told, and can set it back to active.
          </span>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {canChangeSetting && (
            <Link
              to={`/practices/${practiceId}/settings`}
              data-testid="auto-mastery-notice-settings"
              className="font-medium text-primary hover:underline"
            >
              Practice settings
            </Link>
          )}
          <button
            type="button"
            data-testid="auto-mastery-notice-dismiss"
            onClick={() => dismissal.dismiss('auto-mastery')}
            className={buttonClass('secondary', 'sm', 'gap-1')}
          >
            <X className="h-4 w-4" aria-hidden="true" /> Got it
          </button>
        </div>
      </div>
      <SaveError mutation={dismissal.failureOf('auto-mastery')} fallback="Could not dismiss this." />
    </div>
  );
}
